<template>
  <div class="login-otp-page">
    <!-- Background Animation -->
    <div class="background-animation">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <div class="login-container">
      <div class="login-box">
        <!-- Logo Section -->
        <div class="logo-section d-flex justify-content-center">
          <img src="assets/img/cyanlogo.png" alt="CYAN Waterpark" class="logo">
        </div>

        <!-- OTP Entry Section -->
        <div class="otp-section">
          <h2>{{ $t('login_otp.enter_otp') }}</h2>
          <p>{{ $t('login_otp.otp_sent_to', { mobile: 'XXXXX'+storedMobile }) }}</p>

          <!-- OTP Input -->
          <div class="otp-input-container">
            <input 
              v-for="(digit, index) in otpDigits" 
              :key="index"
              type="text" 
              maxlength="1"
              pattern="\d*"
              inputmode="numeric"
              @input="handleInput($event, index)"
              @keydown="handleKeydown($event, index)"
              ref="otpInputs"
            />
          </div>

          <!-- Timer and Resend Option -->
          <div class="otp-timer" v-if="false">
            <p v-if="timer > 0">
              {{ $t('login_otp.resend_otp') }} in {{ timer }}s
            </p>
            <button 
              v-else 
              @click="resendOTP" 
              class="resend-btn"
            >
              {{ $t('login_otp.resend_otp') }}
            </button>
          </div>

          <!-- Verify Button -->
          <button 
            @click="verifyOTP" 
            class="verify-btn" 
            :disabled="!isOTPComplete"
          >
            {{ $t('login_otp.verify_otp') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import axiosInstance from '@/plugins/axios';
import { useUserStore } from '@/stores/userStore';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

const userStore = useUserStore();
const router = useRouter();
const otpDigits = ref(['', '', '', '']);
const otpInputs = ref([]);
const timer = ref(60);
const storedMobile = ref('');
let timerInterval;

const isOTPComplete = computed(() => 
  otpDigits.value.every(digit => digit !== '')
);

const handleInput = (event, index) => {
  const value = event.target.value.replace(/\D/g, '');
  otpDigits.value[index] = value;

  if (value && index < 3) {
    otpInputs.value[index + 1].focus();
  }
};

const handleKeydown = (event, index) => {
  if (event.key === 'Backspace' && !otpDigits.value[index]) {
    if (index > 0) {
      otpInputs.value[index - 1].focus();
    }
  }
};

const startTimer = () => {
  clearInterval(timerInterval);
  timerInterval = setInterval(() => {
    if (timer.value > 0) {
      timer.value--;
    } else {
      clearInterval(timerInterval);
    }
  }, 1000);
};

const resendOTP = () => {
  const newOTP = Math.floor(1000 + Math.random() * 9000).toString();
  localStorage.setItem('otp', newOTP);
  startTimer();
  // Optional: Send OTP via API or show alert
  alert(`New OTP: ${newOTP}`);
};

const verifyOTP = async () => {
  const mobile = localStorage.getItem('mobile');
  const enteredOTP = otpDigits.value.join('');
  const response = await axiosInstance.post('/api/verify/otp', { otp: enteredOTP, phone: mobile });
  if (response.status !== 200) {
    alert('Invalid OTP. Please try again.');
    otpDigits.value = ['', '', '', ''];
    return;
  }
  userStore.setToken(response.data.access_token);
  userStore.setUser(response.data.user);
  router.push('/');
};

onMounted(() => {
  const mobile = localStorage.getItem('mobile');
  storedMobile.value = mobile ? mobile.slice(-4) : '';
  startTimer();
});

onUnmounted(() => {
  clearInterval(timerInterval);
});
</script>

<style scoped>
.login-otp-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1268B1, #0d4f8c);
}

/* Background Animation Styles (Similar to LoginCyan.vue) */
.background-animation {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 0;
  overflow: hidden;
}

/* Circle Animation Styles */
.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 15s infinite;
}

/* Rest of the styles similar to LoginCyan.vue */

.login-container {
  width: 100%;
  max-width: 450px;
  margin: 20px;
  position: relative;
  z-index: 1;
}

.login-box {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.logo-section {
  text-align: center;
  margin-bottom: 30px;
}

.logo {
  height: 60px;
  width: auto;
}

.otp-section h2 {
  color: #1268B1;
  text-align: center;
  margin-bottom: 15px;
}

.otp-section p {
  text-align: center;
  color: #666;
  margin-bottom: 25px;
}

.otp-input-container {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 25px;
}

.otp-input-container input {
  color: black !important;
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 24px;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  transition: border-color 0.3s ease;
}

.otp-input-container input:focus {
  border-color: #1268B1;
  outline: none;
}

.otp-timer {
  text-align: center;
  margin-bottom: 25px;
  color: #666;
}

.resend-btn {
  background: none;
  border: none;
  color: #1268B1;
  font-weight: bold;
  cursor: pointer;
}

.verify-btn {
  width: 100%;
  padding: 14px;
  background: #1268B1;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.verify-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* RTL Support */
[dir="rtl"] .otp-section h2,
[dir="rtl"] .otp-section p,
[dir="rtl"] .otp-timer {
  text-align: right;
}
input{
  padding: 0 !important;
}
</style>